import React, { useState, useRef, useCallback, useEffect } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { UserIcon, PhotoIcon, TrashIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { memberProfileAPI } from '../../services/api';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import { getCroppedImg, PixelCrop } from '../../utils/cropImage';
import toast from 'react-hot-toast';
import { apiErrorMessage } from '../../utils/apiError';

interface ProfilePhotoUploadProps {
  currentPhotoUrl?: string | null;
  onPhotoChange: (newUrl: string | null) => void;
}

const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const ALLOWED_EXTENSIONS = '.jpg, .jpeg, .png, .webp';

const ProfilePhotoUpload: React.FC<ProfilePhotoUploadProps> = ({
  currentPhotoUrl,
  onPhotoChange,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [imageError, setImageError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Crop modal state
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<PixelCrop | null>(null);
  const [isCropping, setIsCropping] = useState(false);

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return `Invalid file type. Allowed: ${ALLOWED_EXTENSIONS}`;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return `File too large. Maximum size: ${MAX_FILE_SIZE_MB}MB`;
    }
    return null;
  };

  // Selecting a file opens the crop modal — every headshot is squared + centered.
  const handleFileSelect = useCallback((file: File) => {
    const error = validateFile(file);
    if (error) {
      toast.error(error);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setCropSrc(e.target?.result as string);
    };
    reader.onerror = () => toast.error('Failed to read file');
    reader.readAsDataURL(file);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const onCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleApplyCrop = async () => {
    if (!cropSrc || !croppedAreaPixels) return;
    setIsCropping(true);
    try {
      const croppedFile = await getCroppedImg(cropSrc, croppedAreaPixels);
      setSelectedFile(croppedFile);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target?.result as string);
        setImageError(false);
      };
      reader.readAsDataURL(croppedFile);
      setCropSrc(null);
    } catch (err) {
      toast.error('Failed to crop image');
    } finally {
      setIsCropping(false);
    }
  };

  const handleCropCancel = () => {
    setCropSrc(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setIsUploading(true);
    try {
      const response = await memberProfileAPI.uploadProfilePhoto(selectedFile);
      onPhotoChange(response.photo_url);
      setPreviewUrl(null);
      setSelectedFile(null);
      toast.success('Profile photo uploaded');
    } catch (err: any) {
      toast.error(apiErrorMessage(err, 'Failed to upload photo'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!currentPhotoUrl) return;
    if (!window.confirm('Are you sure you want to remove your profile photo?')) return;
    setIsDeleting(true);
    try {
      await memberProfileAPI.deleteProfilePhoto();
      onPhotoChange(null);
      toast.success('Profile photo removed');
    } catch (err: any) {
      toast.error(apiErrorMessage(err, 'Failed to remove photo'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    setPreviewUrl(null);
    setSelectedFile(null);
    setImageError(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const triggerFileInput = () => fileInputRef.current?.click();

  const displayUrl = previewUrl || currentPhotoUrl;
  const hasPreview = previewUrl !== null;

  useEffect(() => {
    setImageError(false);
  }, [displayUrl]);

  return (
    <div className="space-y-4">
      {/* Photo Display (circular — exactly how it appears in the directory) */}
      <div
        className={`relative mx-auto w-28 h-28 rounded-full overflow-hidden border-2 transition-colors ${
          dragActive
            ? 'border-clinic-primary bg-clinic-primary-lighter'
            : 'border-clinic-primary-light bg-clinic-cream'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        {displayUrl && !imageError ? (
          <img
            src={displayUrl}
            alt="Profile"
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {imageError && hasPreview ? (
              <PhotoIcon className="h-14 w-14 text-clinic-primary" />
            ) : (
              <UserIcon className="h-14 w-14 text-clinic-muted" />
            )}
          </div>
        )}

        {dragActive && (
          <div className="absolute inset-0 bg-clinic-primary/20 flex items-center justify-center">
            <PhotoIcon className="h-8 w-8 text-clinic-primary" />
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_TYPES.join(',')}
        onChange={handleInputChange}
        className="hidden"
      />

      {/* Action Buttons */}
      {hasPreview ? (
        <div className="flex justify-center space-x-2">
          <Button variant="ghost" size="sm" onClick={handleCancel} disabled={isUploading}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleUpload}
            isLoading={isUploading}
            leftIcon={!isUploading ? <ArrowUpTrayIcon className="h-4 w-4" /> : undefined}
          >
            {isUploading ? 'Uploading...' : 'Save Photo'}
          </Button>
        </div>
      ) : (
        <div className="flex justify-center space-x-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={triggerFileInput}
            leftIcon={<PhotoIcon className="h-4 w-4" />}
          >
            {currentPhotoUrl ? 'Change' : 'Upload'}
          </Button>
          {currentPhotoUrl && (
            <Button variant="danger" size="sm" onClick={handleDelete} isLoading={isDeleting}>
              {isDeleting ? <span className="sr-only">Removing...</span> : <TrashIcon className="h-4 w-4" />}
            </Button>
          )}
        </div>
      )}

      {/* Help text — headshot guidance */}
      <p className="text-xs text-clinic-muted text-center">
        {hasPreview
          ? 'Preview shown above. Click Save to upload.'
          : 'Use a clear, well-lit headshot with your face centered. Drag & drop or click to upload. '}
        {!hasPreview && `Max ${MAX_FILE_SIZE_MB}MB.`}
      </p>

      {/* Crop modal — forces a square, centered headshot */}
      <Modal
        isOpen={!!cropSrc}
        onClose={handleCropCancel}
        title="Crop your headshot"
        description="Center your face and zoom to frame a clear, professional headshot."
        size="md"
        closeOnOverlayClick={false}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={handleCropCancel} disabled={isCropping}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleApplyCrop} isLoading={isCropping}>
              {isCropping ? 'Applying…' : 'Apply Crop'}
            </Button>
          </>
        }
      >
        {cropSrc && (
          <div className="space-y-4">
            <div className="relative w-full h-72 bg-clinic-dark rounded-lg overflow-hidden">
              <Cropper
                image={cropSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-clinic-muted">Zoom</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                aria-label="Zoom"
                className="flex-1 accent-clinic-primary"
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ProfilePhotoUpload;
